import { cx } from "@emotion/css";
import Color from "@tiptap/extension-color";
import { DragHandle } from "@tiptap/extension-drag-handle-react";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TableKit } from "@tiptap/extension-table";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import {
	Bold,
	GripVertical,
	Italic,
	Link as LinkIcon,
	Strikethrough,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { blobUrlToCompressedFile } from "src/helpers/blobUrlToCompressedFile";
import newApi from "src/newApi";
import "src/styles/tiptapEditor.scss";
import ImageResize from "tiptap-extension-resize-image";
import CalloutExtension from "./extensions/CalloutExtension";
import { TableFigureExtensions } from "./extensions/TableFigureExtension";
import TipTapLinkModal from "./TipTapLinkModal";
import TipTapToolbar from "./TipTapToolbar";

async function uploadLocalImages(editor) {
	const json = editor.getJSON();
	let changed = false;

	async function walkNodes(node) {
		if (node.type === "imageResize" || node.type === "image") {
			const src = node.attrs?.src;
			if (src?.startsWith("blob:") || src?.startsWith("data:")) {
				try {
					const file = await blobUrlToCompressedFile(src);
					const result = await newApi.uploadImage(file);
					if (result?.url) {
						node.attrs.src = result.url;
						changed = true;
					}
				} catch {
					// upload failed — leave blob for now
				}
			}
		}
		if (node.content) {
			for (const child of node.content) {
				await walkNodes(child);
			}
		}
	}

	await walkNodes(json);

	if (changed) {
		editor.commands.setContent(json);
	}
}

export default function TipTapEditor({
	initialHtml = "",
	onSubmit = undefined,
	submitText = undefined,
	cancelText = undefined,
	onCancel = undefined,
	placeholder = undefined,
	className = undefined,
	error = undefined,
	helperText = undefined,
	onUpdate = undefined,
	hideActionsWhenClean = false,
	features = undefined,
	inlineFirst = false,
	label = undefined,
	uploadImagesOnInsert = false,
	/** Dodatkowe przyciski na koncu toolbara (np. akcje admina). */
	toolbarActions = undefined,
	"data-test-id": dataTestId = "",
}) {
	const { t } = useTranslation();
	const [isUploading, setIsUploading] = useState(false);
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const initialHtmlRef = useRef(initialHtml || "");

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: { levels: [1, 2, 3, 4] },
			}),
			Underline,
			Link.configure({
				openOnClick: false,
				autolink: true,
			}),
			TextAlign.configure({
				types: ["heading", "paragraph"],
			}),
			Superscript,
			Subscript,
			ImageResize.configure({
				inline: false,
				allowBase64: true,
			}),
			TaskList,
			TaskItem.configure({
				nested: true,
			}),
			Highlight.configure({
				multicolor: true,
			}),
			TextStyle,
			Color,
			CalloutExtension,
			...(features?.includes("table")
				? [
						TableKit.configure({ table: { resizable: true } }),
						...TableFigureExtensions,
					]
				: []),
			Placeholder.configure({
				// includeChildren: placeholder też w pustym tytule / legendzie tabeli (w figure)
				includeChildren: true,
				placeholder: ({ node }) => {
					if (node.type.name === "tableTitle") {
						return t("editor.table.titlePlaceholder", "Tytuł tabeli");
					}
					if (node.type.name === "tableNote") {
						return t(
							"editor.table.notePlaceholder",
							"Legenda, np. * objaśnienie",
						);
					}
					return placeholder || t("editor.placeholder", "Zacznij pisać...");
				},
			}),
		],
		content: initialHtml || "",
		editorProps: {
			// Surowy kod HTML (np. z VS Code) przychodzi jako text/plain, więc bez tego wkleiłby się jako tekst z tagami
			handlePaste: (_view, event) => {
				const text = event.clipboardData?.getData("text/plain").trim();
				if (!text || !/^<[a-z!][\s\S]*>$/i.test(text)) return false;
				editor?.commands.insertContent(text);
				return true;
			},
		},
		onUpdate: ({ editor }) => {
			const html = editor.getHTML();
			setIsDirty(html !== initialHtmlRef.current);
			if (onUpdate) {
				onUpdate(html);
			}
		},
	});

	useEffect(() => {
		if (!editor) return;
		const next = initialHtml || "";
		initialHtmlRef.current = next;
		if (editor.getHTML() !== next) {
			// sync z propsa to nie edycja usera — bez onUpdate (inaczej formularz od razu "dirty")
			editor.commands.setContent(next, { emitUpdate: false });
		}
		setIsDirty(false);
	}, [editor, initialHtml]);

	const handleSubmit = async () => {
		if (!editor) return;

		try {
			setIsUploading(true);
			await uploadLocalImages(editor);
			const html = editor.getHTML();
			await onSubmit?.(html);
			initialHtmlRef.current = html;
			setIsDirty(false);
		} catch {
			toast.error(t("editor.saveError", "Nie udało się zapisać"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 5000,
			});
		} finally {
			setIsUploading(false);
		}
	};

	const handleCancel = () => {
		if (editor) {
			editor.commands.setContent(initialHtmlRef.current || "");
		}
		setIsDirty(false);
		onCancel?.();
	};

	return (
		<div
			className={cx("tiptap-editor-wrapper", className, {
				"tiptap-editor-wrapper--error": error,
			})}
			data-test-id={dataTestId}
		>
			<TipTapToolbar
				editor={editor}
				features={features}
				inlineFirst={inlineFirst}
				label={label}
				uploadImagesOnInsert={uploadImagesOnInsert}
				actions={toolbarActions}
			/>
			<div className="tiptap-editor-content">
				{editor && (
					<DragHandle editor={editor}>
						<div className="tiptap-drag-handle">
							<GripVertical size={16} />
						</div>
					</DragHandle>
				)}
				{editor && (
					<BubbleMenu
						editor={editor}
						tippyOptions={{
							duration: 100,
							placement: "bottom-start",
							popperOptions: {
								modifiers: [{ name: "flip", enabled: false }],
							},
						}}
						className="tiptap-toolbar tiptap-bubble-menu"
					>
						<div className="tiptap-toolbar__group">
							<button
								type="button"
								className={`tiptap-toolbar__btn${editor.isActive("bold") ? " tiptap-toolbar__btn--active" : ""}`}
								onClick={() => editor.chain().focus().toggleBold().run()}
							>
								<Bold size={16} />
							</button>
							<button
								type="button"
								className={`tiptap-toolbar__btn${editor.isActive("italic") ? " tiptap-toolbar__btn--active" : ""}`}
								onClick={() => editor.chain().focus().toggleItalic().run()}
							>
								<Italic size={16} />
							</button>
							<button
								type="button"
								className={`tiptap-toolbar__btn${editor.isActive("strike") ? " tiptap-toolbar__btn--active" : ""}`}
								onClick={() => editor.chain().focus().toggleStrike().run()}
							>
								<Strikethrough size={16} />
							</button>
							<button
								type="button"
								className={`tiptap-toolbar__btn${editor.isActive("link") ? " tiptap-toolbar__btn--active" : ""}`}
								onClick={() => setShowLinkModal(true)}
							>
								<LinkIcon size={16} />
							</button>
						</div>
					</BubbleMenu>
				)}
				<div data-test-id="tiptap-editor">
					<EditorContent editor={editor} />
				</div>
			</div>
			{helperText && (
				<div className="tiptap-editor-error-text">{helperText}</div>
			)}
			{onSubmit && (!hideActionsWhenClean || isDirty) && (
				<div className="tiptap-editor-actions">
					{cancelText && (
						<TextPrimaryButton onClick={handleCancel}>
							{cancelText}
						</TextPrimaryButton>
					)}
					<PrimaryButton
						data-test-id="note-save-button"
						onClick={handleSubmit}
						disabled={isUploading}
					>
						{isUploading
							? t("editor.uploading", "Zapisywanie...")
							: submitText || t("commands.add")}
					</PrimaryButton>
				</div>
			)}
			{showLinkModal && (
				<TipTapLinkModal
					editor={editor}
					onClose={() => setShowLinkModal(false)}
				/>
			)}
		</div>
	);
}
